import "custom-event-polyfill";
import Handlebars from "handlebars/dist/cjs/handlebars";
import _ from "lodash";
import * as React from "react";
import Slider from "react-slick";
import { arrayMove, SortableContainer, SortableElement, SortableHandle } from "react-sortable-hoc";
import "slick-carousel/slick/slick-theme.css";
import "slick-carousel/slick/slick.css";
import "../../common/styles/balloon.css";
import "../../common/styles/presenceFramework.css";
import "../banner.css";
import { convertToMilliseconds } from "../classes/MiscHelper";
import { isEmptyString } from "../classes/StringHelper";
import { ArrowType, IBannerSlide, ISettings, loadBanner, saveBanner, uploadFile } from "../data/DataService";
import Checkbox from "./Checkbox";
import ColorPicker from "./ColorPicker";
import { Crop } from "./Crop";
import Hover from "./Hover";
import Loader from "./Loader";
import Modal from "./Modal";
import RadioButton from "./RadioButton";

const DragHandle = SortableHandle(() => <div className="CMS_button CMS_bSimple CMS_bSquare"><em className="fa fa-sort"></em></div>);

const SortableRow = SortableElement((props: any) => {
  return (
    <tr>
      <td><DragHandle /></td>
      <td className="BAN_imageCol">
        <Loader isLoading={!props.slide.isLoaded} className="slide-loader">
          <img
            src={props.getUrl(props.slide.cropped.path)}
            style={{ width: "100px" }}
            onLoad={() => {
              if (props.onImageLoad) {
                props.onImageLoad(props.slide);
              }
            }}
          />
        </Loader>
      </td>
      <td>
        <div className="truncate">{props.slide.title ? props.slide.title : props.slide.original.displayName}</div>
      </td>
      <td>
        <a
          href="#"
          className="CMS_button CMS_bSquare CMS_bWarn"
          onClick={(e) => {
            e.preventDefault();
            props.editSlide(props.slide);
          }}
        >
          <em className="fa fa-pencil"></em>
        </a>&nbsp;
        <a
          href="#"
          className="CMS_button CMS_bSquare CMS_bCancel"
          onClick={(e) => {
            e.preventDefault();
            if (confirm("Remove image?")) {
              props.removeSlide(props.slide);
            }
          }}
        >
          <em className="fa fa-trash"></em>
        </a>
      </td>
    </tr>
  );
});

const SortableTable = SortableContainer((props: any) => {
  return (
    <table className="CMS_table no-select">
      <thead>
        <tr>
          <th className="CMS_tButton CMS_tSingle"></th>
          <th>Preview</th>
          <th>Name</th>
          <th className="CMS_tButton CMS_tDouble"></th>
        </tr>
      </thead>
      <tbody>
        {(props.slides || []).length > 0
          ? _.map(props.slides, (slide, index) => (
            <SortableRow
              key={`item-${index}`}
              index={index}
              imageDimensions={props.imageDimensions}
              removeSlide={props.removeSlide}
              editSlide={props.editSlide}
              slide={slide}
              getUrl={props.getUrl}
              onImageLoad={props.onImageLoad}
              helperClass={props.helperClass}
            />
          ))
          : <tr><td colSpan={4} style={{ textAlign: "center" }}>No Slides</td></tr>}
      </tbody>
    </table>
  );
});

interface IProps extends React.Props<Banner> {
  cdnLocation: string;
  dangerousHtmlRegexPattern: string;
  isCarouselMode: boolean;
  portletInstanceId: string;
}

interface IState {
  captionTemplate: Handlebars.TemplateDelegate;
  captionValid: boolean;
  crop: boolean;
  currentIndex: number;
  currentSlide: IBannerSlide;
  dangerousHtmlRegex: RegExp;
  error: {
    isRecoverable: boolean;
    message: string;
    onClose?: () => void;
  };
  hasUnsavedChanges: boolean;
  isAddOpen: boolean;
  isBannerOptionsOpen: boolean;
  isEditOpen: boolean;
  isImageLoading: boolean;
  isLoading: boolean;
  isPaused: boolean;
  isPreviewMode: boolean;
  isSliderPaused: boolean;
  isStyleOptionsOpen: boolean;
  reCropIndex: number;
  settings: ISettings;
  slides: IBannerSlide[];
  tempSettings: ISettings;
  upload: {
    image: File;
    height: number;
    width: number;
    url: string;
  };
}

const black = "#000";
const white = "#fff";
const defaultWidth = 800;
const defaultHeight = 450;

const sliderSpeeds = [5, 10, 15, 20, 25];

export default class Banner extends React.Component<IProps, IState> {
  public state = {
    captionTemplate: null,
    captionValid: true,
    crop: false,
    currentIndex: 0,
    currentSlide: null,
    dangerousHtmlRegex: new RegExp(this.props.dangerousHtmlRegexPattern, "i"),
    error: null,
    hasUnsavedChanges: false,
    isAddOpen: false,
    isBannerOptionsOpen: false,
    isEditOpen: false,
    isImageLoading: false,
    isLoading: false,
    isPaused: false,
    isPreviewMode: false,
    isSliderPaused: false,
    isStyleOptionsOpen: false,
    reCropIndex: -1,
    settings: { fade: false } as ISettings,
    slides: [],
    tempSettings: {} as ISettings,
    upload: null,
  };

  private slickSlider: any;
  private refSliderContainer: any;
  private uploadInput: HTMLInputElement;

  public constructor(props, context) {
    super(props, context);
  }

  public componentDidMount() {
    if ((this.props.portletInstanceId || "").trim() !== "") {
      this.setState({ isLoading: true });
      loadBanner(this.props.portletInstanceId)
        .then((details) => {
          _.forEach((details.slides || []), (slide) => slide.isLoaded = false);
          this.setSettings(details.settings, () => {
            const initialSlideIndex = this.state.settings.randomStartImage
              ? _.random(0, details.slides.length - 1)
              : 0;

            this.setState({
              currentIndex: initialSlideIndex,
              isLoading: false,
              slides: details.slides || [],
            });
          });
        })
        .catch(() => {
          this.showError(!this.props.isCarouselMode ? "Could not load banner" : "", false);
        });
    } else {
      this.setSettings({});
    }

    window.addEventListener("beforeunload", this.onUnload.bind(this));
  }

  public componentDidUpdate() {
    this.fixAccessibility();
  }

  public componentWillUnmount() {
    window.removeEventListener("beforeunload", this.onUnload.bind(this));
  }

  public render() {
    return (
      <div className="PO-banner" style={this.state.isLoading ? { minHeight: "100px" } : {}}>
        {this.state.error && !this.state.error.isRecoverable
          ? null
          : <Loader isLoading={this.state.isLoading}>
            {!this.props.isCarouselMode ? (
              <div className={"CMS_element CMS_eMaxWidth"}>
                <div>
                  <div className="CMS_title CMS_T4">
                    <em className="fa fa-image"></em> Banner
                    <a
                      className={`CMS_button CMS_bPreview pull-right ${this.state.slides.length === 0 ? "disabled" : ""}`}
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        if (this.state.slides.length > 0) {
                          this.setState({ isPreviewMode: !this.state.isPreviewMode });
                        }
                      }}
                    >
                      {this.state.isPreviewMode ? <em className="fa fa-eye-slash" aria-hidden="true"></em> : <em className="fa fa-eye" aria-hidden="true"></em>} Preview
                    </a>
                  </div>
                  {this.state.isPreviewMode
                    ? this.slider
                    : <div>
                      <div className="CMS_e_row">
                        <div className="CMS_e_row">
                          <div className="CMS_tableHeading">
                            <div className="CMS_title CMS_T5">Images</div>
                            <div className="CMS_buttonGroup">
                              <input
                                type="file"
                                accept="image/*"
                                ref={(ref) => this.uploadInput = ref}
                                hidden
                                onChange={(e) => {
                                  const file = e.target.files[0];
                                  if (!/^image\/.*/i.test(file.type)) {
                                    alert(`Invalid image type: ${file.type}`);
                                    return;
                                  }

                                  this.setState({ isLoading: true });
                                  const url = URL.createObjectURL(file);
                                  const img = new Image();
                                  img.onload = () => {
                                    const currentSlide = this.newSlide;
                                    this.setState({
                                      currentSlide,
                                      isLoading: false,
                                      upload: {
                                        height: img.height,
                                        image: file,
                                        width: img.width,
                                        url,
                                      },
                                    });
                                    this.addSlide();
                                  };
                                  img.src = url;
                                }}
                              />
                              <a
                                className="CMS_button CMS_bGo"
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  this.uploadInput.click();
                                }}
                              >
                                <em className="fa fa-plus" aria-hidden="true"></em> Add Image
                              </a>
                            </div>
                          </div>
                        </div>
                        <div className="CMS_e_row">
                          <SortableTable
                            imageDimensions={this.state.settings.dimensions}
                            onSortEnd={this.onSortEnd}
                            slides={this.state.slides}
                            removeSlide={this.removeSlide.bind(this)}
                            editSlide={this.editSlide.bind(this)}
                            lockToContainerEdges={true}
                            pressDelay={50}
                            useDragHandle={true}
                            useWindowAsScrollContainer={true}
                            helperClass="banner-sortable-helper"
                            getUrl={this.getUrl.bind(this)}
                            onImageLoad={this.onImageLoad.bind(this)}
                          />
                        </div>
                        <div className="CMS_e_row">
                          <div className="CMS_buttonGroup">
                            <a
                              className="CMS_button CMS_bAction BAN_openStyleOptions"
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                this.setTempSettings();
                                this.setState({ isStyleOptionsOpen: true });
                              }}
                            >
                              <em className="fa fa-paint-brush" aria-hidden="true"></em> Style Options
                            </a>&nbsp;
                            <a
                              className="CMS_button CMS_bAction BAN_openOptions"
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                this.setTempSettings();
                                this.setState({ isBannerOptionsOpen: true });
                              }}
                            >
                              <em className="fa fa-gears" aria-hidden="true"></em> Settings
                            </a>
                          </div>
                        </div>
                        <div className="CMS_buttonGroup CMS_bg-R">
                          <a
                            className="CMS_button CMS_bAction"
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              this.setState({ isLoading: true });
                              saveBanner(this.props.portletInstanceId, {
                                settings: this.state.settings,
                                slides: this.state.slides,
                              })
                                .then(() => {
                                  this.setState({
                                    hasUnsavedChanges: false,
                                    isLoading: false,
                                  });
                                  this.closePortlet();
                                })
                                .catch(() => {
                                  this.setState({ isLoading: false });
                                  this.showError("Could not save banner", true);
                                });
                            }}
                          >
                            Update
                          </a>&nbsp;
                          <a
                            className="CMS_button CMS_bCancel"
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              this.closePortlet();
                            }}
                          >
                            Cancel
                          </a>
                        </div>
                      </div>
                    </div>
                  }
                </div>
                {this.errorModal}
                {this.addEditModal}
                {this.styleOptionsModal}
                {this.bannerOptionsModal}
              </div>
            ) : this.slider}
          </Loader>}
      </div>
    );
  }

  private closePortlet() {
    window.dispatchEvent(new CustomEvent("onbannerclose"));
  }

  private onUnload(e: BeforeUnloadEvent) {
    if (this.state.hasUnsavedChanges === false) {
      return;
    }

    const confirmationMessage = "Do you want to leave this page?\nAll unsaved changes will be lost.";
    (e || window.event).returnValue = confirmationMessage; // Gecko + IE
    return confirmationMessage; // Gecko + Webkit, Safari, Chrome, etc
  }

  private showError(message: string, isRecoverable: boolean) {
    this.setState({
      error: {
        isRecoverable,
        message,
        onClose: !isRecoverable ? this.closePortlet.bind(true) : null,
      },
    });
  }

  private closeErrorModal() {
    const onClose = this.state.error.onClose;
    if (this.state.error.isRecoverable) {
      this.setState({ error: null });
    }
    if (onClose) {
      onClose();
    }
  }

  private get errorModal() {
    return this.state.error ? (
      <Modal
        className="banner-modal error-modal"
        isOpen={!isEmptyString(this.state.error.message)}
        onRequestClose={this.closeErrorModal.bind(this)}
      >
        <div className="CMS_element">
          <div className="CMS_title CMS_T4">Error</div>
          <div className="CMS_element CMS_eMaxWidth">
            {this.state.error.message}
          </div>
          <div className="CMS_buttonGroup CMS_bg-R">
            <a
              className="CMS_button CMS_bAction"
              href="#"
              onClick={this.closeErrorModal.bind(this)}
            >
              OK
            </a>
          </div>
        </div>
      </Modal>
    ) : null;
  }

  private get addEditModal() {
    return (
      <Modal
        className="banner-modal"
        isClosable={!this.state.isLoading && this.state.reCropIndex < 0}
        isOpen={this.state.isEditOpen || this.state.isAddOpen}
        onRequestClose={this.closeAddEdit.bind(this)}
        shouldCloseOnEsc={true}
      >
        <Loader isLoading={this.state.isLoading}>
          {this.state.isEditOpen || this.state.isAddOpen ? <div className="CMS_element">
            <div className="CMS_title CMS_T4"><em className="fa fa-image"></em> {this.state.isAddOpen ? "Add" : "Edit"} Image</div>
            <div className="CMS_splitSection CMS_halfContent">
              <div>
                <div className="CMS_e_row">
                  <div className="CMS_title CMS_T6">Caption Title</div>
                  <input
                    type="text"
                    className="CMS_input"
                    value={this.state.currentSlide.title || ""}
                    onChange={(e) => {
                      const currentSlide = _.clone(this.state.currentSlide);
                      currentSlide.title = e.currentTarget.value;
                      this.setState({ currentSlide });
                    }}
                    onBlur={(e) => {
                      const currentSlide = _.clone(this.state.currentSlide);
                      currentSlide.title = currentSlide.title.trim();
                      this.setState({ currentSlide });
                    }}
                  />
                </div>
                <div className="CMS_e_row">
                  <div className="CMS_title CMS_T6">Caption Description</div>
                  <input
                    type="text"
                    className="CMS_input"
                    value={this.state.currentSlide.caption || ""}
                    onChange={(e) => {
                      const currentSlide = _.clone(this.state.currentSlide);
                      currentSlide.caption = e.currentTarget.value;
                      this.setState({ currentSlide });
                    }}
                  />
                </div>
                <div className="CMS_e_row">
                  <div className="CMS_title CMS_T6">URL</div>
                  <input
                    type="text"
                    className="CMS_input"
                    value={this.state.currentSlide.url || ""}
                    onChange={(e) => {
                      const currentSlide = _.clone(this.state.currentSlide);
                      currentSlide.url = e.currentTarget.value.trim();
                      this.setState({ currentSlide });
                    }}
                  />
                </div>
                <div className="CMS_e_row">
                  <div className="CMS_title CMS_T6">Alt Text</div>
                  <input
                    type="text"
                    className="CMS_input"
                    value={this.state.currentSlide.alt || ""}
                    onChange={(e) => {
                      const currentSlide = _.clone(this.state.currentSlide);
                      currentSlide.alt = e.currentTarget.value;
                      this.setState({ currentSlide });
                    }}
                    onBlur={(e) => {
                      const currentSlide = _.clone(this.state.currentSlide);
                      currentSlide.alt = currentSlide.alt.trim();
                      this.setState({ currentSlide });
                    }}
                  />
                  {this.state.currentSlide.alt != null && isEmptyString(this.state.currentSlide.alt)
                    ? <div className="validationError CMS_inlineAlerts CMS_error">Alt text is required</div> : null}
                </div>
                <div className="CMS_e_row">
                  <div className="CMS_title CMS_T6">Action On Image Click</div>
                  <RadioButton
                    checked={this.state.currentSlide.imageAction === "_self"}
                    disabled={isEmptyString(this.state.currentSlide.url)}
                    onChange={(imageAction) => {
                      const currentSlide = { ...this.state.currentSlide };
                      currentSlide.imageAction = imageAction;
                      this.setState({ currentSlide });
                    }}
                    value={"_self"}
                  >
                    Open URL in current window
                  </RadioButton>&nbsp;
                  <RadioButton
                    checked={this.state.currentSlide.imageAction === "_blank"}
                    disabled={isEmptyString(this.state.currentSlide.url)}
                    onChange={(imageAction) => {
                      const currentSlide = { ...this.state.currentSlide };
                      currentSlide.imageAction = imageAction;
                      this.setState({ currentSlide });
                    }}
                    value={"_blank"}
                  >
                    Open URL in new window
                  </RadioButton>
                </div>
              </div>
              <div>
                <div className="CMS_e_row">
                  <Crop
                    aspectRatio={this.aspectRatio}
                    crop={this.state.crop}
                    image={this.currentImage}
                    onCrop={this.saveImages.bind(this)}
                    onReady={() => this.setState({ isLoading: false })}
                    transform={this.state.currentSlide.transform}
                    maxWidth={this.state.settings.dimensions.width}
                    maxHeight={this.state.settings.dimensions.height}
                  />
                </div>
              </div>
            </div>
            <div className="CMS_buttonGroup CMS_bg-R">
              <a
                className="CMS_button CMS_bAction"
                href="#"
                onClick={(e) => {
                  e.preventDefault();

                  // Hack to only display the alt message required after
                  // the button click
                  if (this.state.currentSlide.alt == null) {
                    const currentSlide = _.clone(this.state.currentSlide);
                    currentSlide.alt = "";
                    this.setState({ currentSlide });
                  }

                  if (!this.isAddUpdateImageValid) {
                    return;
                  }
                  this.setState({
                    crop: true,
                  });
                }}
              >
                {this.state.isAddOpen ? "Add" : "Update"}
              </a>&nbsp;
              {this.state.reCropIndex < 0 ?
                <a
                  className="CMS_button CMS_bCancel"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    this.closeAddEdit();
                  }}
                >
                  Close
                </a> : null}
            </div>
          </div> : null}
        </Loader>
      </Modal>
    );
  }

  private get isAddUpdateImageValid() {
    return !isEmptyString(this.state.currentSlide.alt);
  }

  private get styleOptionsModal() {
    return (
      <Modal
        className="banner-modal"
        isOpen={this.state.isStyleOptionsOpen}
        onRequestClose={() => {
          this.setState({ isStyleOptionsOpen: false });
        }}
        shouldCloseOnEsc={true}
      >
        {this.state.isStyleOptionsOpen ? <div className="CMS_element">
          <div className="CMS_title CMS_T4"><em className="fa fa-paint-brush" aria-hidden="true"></em> Style Options</div>
          <div className="CMS_e_row">
            <div className="CMS_splitSection CMS_halfContent">
              <div>
                <div className="CMS_e_row">
                  <div className="CMS_title CMS_T6">Arrows</div>
                  {_.map(Object.values(ArrowType).map((k) => ArrowType[k]).filter((v) => typeof v === "number"),
                    (type, index) => (
                      <span key={`arrow-type-${index}`}>
                        <RadioButton
                          checked={this.state.tempSettings.arrowType === type}
                          disabled={!this.state.tempSettings.arrows}
                          onChange={(arrowType: ArrowType) => {
                            const tempSettings = { ...this.state.tempSettings };
                            tempSettings.arrowType = arrowType;
                            this.setState({ tempSettings });
                          }}
                          value={type}
                        >
                          <div className="BAN_iconChoice">
                            {this.getArrowIcon(type, true, black)}
                            &nbsp;{this.getArrowIcon(type, false, black)}
                          </div>
                        </RadioButton>&nbsp;
                      </span>
                    ))}
                </div>
                <div className="CMS_e_row">
                  <div className="CMS_splitSection CMS_halfContent">
                    <div>
                      <div className="CMS_title CMS_T6">Arrow Color</div>
                      <ColorPicker
                        color={this.state.tempSettings.color}
                        onChange={(color: string) => {
                          const tempSettings = { ...this.state.tempSettings };
                          tempSettings.color = color;
                          this.setState({ tempSettings });
                        }}
                      />
                    </div>
                    <div>
                      <div className="CMS_title CMS_T6">On Hover</div>
                      <ColorPicker
                        color={this.state.tempSettings.hoverColor}
                        onChange={(hoverColor: string) => {
                          const tempSettings = { ...this.state.tempSettings };
                          tempSettings.hoverColor = hoverColor;
                          this.setState({ tempSettings });
                        }}
                      />
                    </div>
                  </div>
                </div>
                <div className="CMS_title CMS_T6">Arrow Background</div>
                <div className="CMS_e_row">
                  <div className="CMS_splitSection CMS_halfContent">
                    <div>
                      <div className="CMS_title CMS_T6">Background Color</div>
                      <ColorPicker
                        color={this.state.tempSettings.backgroundColor}
                        onChange={(color: string) => {
                          const tempSettings = { ...this.state.tempSettings };
                          tempSettings.backgroundColor = color;
                          this.setState({ tempSettings });
                        }}
                      />
                    </div>
                    <div>
                      <div className="CMS_title CMS_T6">On Hover</div>
                      <ColorPicker
                        color={this.state.tempSettings.backgroundHoverColor}
                        onChange={(hoverColor: string) => {
                          const tempSettings = { ...this.state.tempSettings };
                          tempSettings.backgroundHoverColor = hoverColor;
                          this.setState({ tempSettings });
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <div className="CMS_e_row">
                  <div className="CMS_title CMS_T6">Banner Caption</div>
                  <Checkbox
                    checked={this.state.tempSettings.customCaption}
                    onChange={(customCaption: boolean) => {
                      const tempSettings = { ...this.state.tempSettings };
                      tempSettings.customCaption = customCaption;
                      this.setState({ tempSettings });
                    }}
                    style={{ overflow: "visible" }}
                  >
                    Enable custom caption HTML&nbsp;
                    <span
                      data-balloon-length="large"
                      data-balloon-pos="up"
                      data-balloon="The caption HTML supports handlebars templates. The following variables are available for binding: altText, caption, title, url, urlTarget. Example: <b>{{caption}}</b>"
                    >
                      <em
                        className="fa fa-info-circle"
                        aria-hidden="true"
                        style={{ color: "#339af0" }}
                      >
                      </em>
                    </span>
                  </Checkbox>
                </div>
                <div className="CMS_e_row">
                  <textarea
                    className="CMS_input"
                    disabled={!this.state.tempSettings.customCaption}
                    onChange={(e) => {
                      this.setState({ captionValid: !this.state.dangerousHtmlRegex.test(e.target.value) });
                      const tempSettings = { ...this.state.tempSettings };
                      tempSettings.caption = e.target.value;
                      this.setState({ tempSettings });
                    }}
                    value={this.state.tempSettings.caption}
                  />
                  {
                    this.state.captionValid
                    ? null
                    : <div className="validationError CMS_inlineAlerts CMS_error">
                      The custom caption cannot contain JavaScript.
                    </div>
                  }
                </div>
              </div>
            </div>
          </div>
          <div className="CMS_buttonGroup CMS_bg-R">
            <a
              className={"CMS_button CMS_bAction" + (this.state.captionValid ? "" : " disabled")}
              href="#"
              onClick={(e) => {
                e.preventDefault();
                if (!this.state.captionValid) {
                  return;
                }
                this.setState({
                  hasUnsavedChanges: true,
                  isStyleOptionsOpen: false,
                });
                this.setSettings(this.state.tempSettings);
              }}
            >
              Update
            </a>&nbsp;
            <a
              className="CMS_button CMS_bCancel"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                this.setState({ isStyleOptionsOpen: false });
              }}
            >
              Close
            </a>
          </div>
        </div> : null}
      </Modal>
    );
  }

  private get bannerOptionsModal() {
    return (
      <Modal
        className="banner-modal"
        isOpen={this.state.isBannerOptionsOpen}
        onRequestClose={() => {
          this.setState({ isBannerOptionsOpen: false });
        }}
        shouldCloseOnEsc={true}
      >
        {this.state.isBannerOptionsOpen ? <div className="CMS_element">
          <div className="CMS_title CMS_T4"><em className="fa fa-gears" aria-hidden="true"></em> Settings</div>
          <div className="CMS_e_row">
            <div className="CMS_splitSection CMS_halfContent">
              <div>
                <div className="CMS_title CMS_T6">Banner Maximum Dimensions</div>
                <div className="CMS_e_row">
                  <div className="BAN_ratio">
                    <div className="CMS_e_row">
                      <div className="CMS_inputWDNumbers CMS_inputWDimension CMS_inputSmall">
                        <input
                          type="number"
                          className="CMS_input"
                          placeholder="Width"
                          min={1}
                          value={this.state.tempSettings.dimensions.width}
                          onChange={(e) => {
                            const tempSettings = { ...this.state.tempSettings };
                            tempSettings.dimensions = {
                              height: tempSettings.dimensions.height,
                              width: parseInt(e.target.value, 10),
                            };
                            this.setState({ tempSettings });
                          }}
                        />
                        <span><em className="fa fa-times" aria-hidden="true"></em></span>
                        <input
                          type="number"
                          className="CMS_input"
                          placeholder="Height"
                          min={1}
                          value={this.state.tempSettings.dimensions.height}
                          onChange={(e) => {
                            const tempSettings = { ...this.state.tempSettings };
                            tempSettings.dimensions = {
                              height: parseInt(e.target.value, 10),
                              width: tempSettings.dimensions.width,
                            };
                            this.setState({ tempSettings });
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="CMS_e_row">
                  <div className="CMS_title CMS_T6">Starting Image</div>
                  <RadioButton
                    checked={!this.state.tempSettings.randomStartImage}
                    value={false}
                    onChange={(randomStartImage: boolean) => {
                      const tempSettings = { ...this.state.tempSettings };
                      tempSettings.randomStartImage = randomStartImage;
                      this.setState({ tempSettings });
                    }}
                  >
                    First image
                  </RadioButton>&nbsp;
                  <RadioButton
                    checked={this.state.tempSettings.randomStartImage}
                    value={true}
                    onChange={(randomStartImage: boolean) => {
                      const tempSettings = { ...this.state.tempSettings };
                      tempSettings.randomStartImage = randomStartImage;
                      this.setState({ tempSettings });
                    }}
                  >
                    Randomly select an image
                  </RadioButton>
                </div>
                <div className="CMS_e_row">
                  <div className="CMS_title CMS_T6">Lazy Load</div>
                  <Checkbox
                    checked={this.state.tempSettings.lazyLoad}
                    onChange={(lazyLoad: boolean) => {
                      const tempSettings = { ...this.state.tempSettings };
                      tempSettings.lazyLoad = lazyLoad;
                      this.setState({ tempSettings });
                    }}
                  >
                    Enabled
                  </Checkbox>
                </div>
              </div>
              <div>
                <div className="CMS_e_row">
                  <div className="CMS_title CMS_T6">Slideshow Mode</div>
                  <Checkbox
                    checked={this.state.tempSettings.slideshowMode}
                    onChange={(slideshowMode: boolean) => {
                      const tempSettings = { ...this.state.tempSettings };
                      tempSettings.slideshowMode = slideshowMode;
                      this.setState({ tempSettings });
                    }}
                  >
                    Enabled
                  </Checkbox>
                </div>
                <div className="CMS_e_row">
                  <div className="CMS_title CMS_T6">Animation</div>
                  <RadioButton
                    checked={!this.state.tempSettings.fade}
                    disabled={!this.state.tempSettings.slideshowMode}
                    onChange={(fade: boolean) => {
                      const tempSettings = { ...this.state.tempSettings };
                      tempSettings.fade = fade;
                      this.setState({ tempSettings });
                    }}
                    value={false}
                  >
                    Slide
                  </RadioButton>&nbsp;
                  {/*<RadioButton
                    checked={this.state.tempSettings.fade}
                    disabled={!this.state.tempSettings.slideshowMode}
                    onChange={(fade: boolean) => {
                      const tempSettings = { ...this.state.tempSettings };
                      tempSettings.fade = fade;
                      this.setState({ tempSettings });
                    }}
                    value={true}
                  >
                    Fade
                  </RadioButton>*/}
                </div>
                <div className="CMS_e_row">
                  <div className="CMS_title CMS_T6">Slideshow Speed</div>
                  <select
                    className="CMS_input"
                    disabled={!this.state.tempSettings.slideshowMode}
                    onChange={(e) => {
                      const tempSettings = { ...this.state.tempSettings };
                      tempSettings.speed = parseInt(e.currentTarget.value, 10);
                      this.setState({ tempSettings });
                    }}
                    value={this.state.tempSettings.speed}
                  >
                    {_.map(sliderSpeeds, (value, index) => (
                      <option
                        key={index}
                        value={value}
                      >
                        {`${value} Seconds`}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
          <div className="CMS_buttonGroup CMS_bg-R">
            <a
              className={`CMS_button CMS_bAction ${!this.isDimensionsValid ? "disabled" : ""}`}
              href="#"
              onClick={(e) => {
                e.preventDefault();
                if (!this.isDimensionsValid) {
                  return;
                }
                this.checkDimensions();
                this.setState({
                  hasUnsavedChanges: true,
                  isBannerOptionsOpen: false,
                });
                this.setSettings(this.state.tempSettings);
              }}
            >
              Update
            </a>&nbsp;
            <a
              className="CMS_button CMS_bCancel"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                this.setState({ isBannerOptionsOpen: false });
              }}
            >
              Close
            </a>
          </div>
        </div> : null}
      </Modal>
    );
  }

  private getUrl(path) {
    return !isEmptyString(this.props.cdnLocation)
      ? `${window.location.protocol}//${_.trimEnd(this.props.cdnLocation, "\/\\")}/${_.trimStart(path, "\/\\")}`
      : path;
  }

  private checkDimensions() {
    if (this.state.slides.length === 0) {
      return;
    }
    if (this.state.tempSettings.dimensions.width === this.state.settings.dimensions.width &&
      this.state.tempSettings.dimensions.height === this.state.settings.dimensions.height) {
      return;
    }
    if (confirm("Changing the width or height will require all the images to be re-cropped. Continue?")) {
      this.setState({ reCropIndex: this.state.slides.length }, () => this.nextReCropSlide());
      return;
    }
    const tempSettings = this.state.tempSettings;
    tempSettings.dimensions = {
      height: this.state.settings.dimensions.height,
      width: this.state.settings.dimensions.width,
    };
    this.setState({ tempSettings });
  }

  private nextReCropSlide() {
    let reCropIndex = this.state.reCropIndex;
    if (reCropIndex <= 0) {
      this.setState({
        reCropIndex: -1,
      });
      return;
    }
    --reCropIndex;
    this.editSlide(this.state.slides[reCropIndex]);
    this.setState({
      reCropIndex,
    });
  }

  private clearUpload() {
    this.setState({
      upload: null,
    });
    this.uploadInput.value = null;
  }

  private closeAddEdit() {
    this.setState({ isAddOpen: false, isEditOpen: false });
    this.clearUpload();
  }

  private get currentImage() {
    return this.state.upload ? this.state.upload.url : this.getUrl(this.state.currentSlide.original.path);
  }

  private get aspectRatio() {
    if (!this.state.settings.dimensions) {
      return 0;
    }
    return this.state.settings.dimensions.width / this.state.settings.dimensions.height;
  }

  private get isDimensionsValid() {
    return this.state.tempSettings.dimensions.width > 0
      && this.state.tempSettings.dimensions.height > 0;
  }

  private get newSlide() {
    return {
      id: `${_.now()}${_.random(0, 1000000)}`, // ID is only intended for findIndex
      imageAction: "_self",
      isLoaded: false,
    } as IBannerSlide;
  }

  private onImageLoad(slide) {
    if (slide.isLoaded) {
      return;
    }
    const index = _.findIndex(this.state.slides, (item) => item.id === slide.id);
    const clone = _.clone(slide);
    clone.isLoaded = true;
    const slides = [...this.state.slides];
    slides[index] = clone;
    this.setState({ slides });
  }

  private getPrevButton() {
    const getPrevButton = (isHover: boolean) => <button
      type="button"
      className="CMS_button CMS_square"
      style={{ backgroundColor: isHover ? this.state.settings.backgroundHoverColor : this.state.settings.backgroundColor }}
      onClick={() => {
        if (!this.slickSlider) {
          return;
        }
        this.slickSlider.slickPrev();
      }}
    >
      {this.getArrowIcon(
        this.state.settings.arrowType,
        true,
        isHover ? this.state.settings.hoverColor : this.state.settings.color,
      )}
      <span className="offScreen">Previous image</span>
    </button>;

    return <Hover
      onMouseOut={() => getPrevButton(false)}
      onMouseOver={() => getPrevButton(true)}
    />;
  }

  private getPauseButton() {
    const getPauseButton = (isHover: boolean) => <button
      type="button"
      className="slick-pause CMS_button CMS_square CMS_bMarginL"
      style={{ backgroundColor: isHover ? this.state.settings.backgroundHoverColor : this.state.settings.backgroundColor }}
      onClick={() => {
        if (!this.slickSlider) {
          return;
        }
        if (this.state.isPaused) {
          this.slickSlider.slickPlay();
        } else {
          this.slickSlider.slickPause();
        }
        this.setState({ isPaused: !this.state.isPaused });
      }}
    >
      <em
        className={`fa ${this.state.isPaused ? "fa-play" : "fa-pause"}`}
        style={{ color: isHover ? this.state.settings.hoverColor : this.state.settings.color }}
      ></em>
      <span className="offScreen">{this.state.isPaused ? "Play" : "Pause"}</span>
    </button>;

    return <Hover
      onMouseOut={() => getPauseButton(false)}
      onMouseOver={() => getPauseButton(true)}
    />;
  }

  private getNextButton() {
    const getNextButton = (isHover: boolean) => <button
      type="button"
      className="CMS_button CMS_square CMS_bMarginL"
      style={{ backgroundColor: isHover ? this.state.settings.backgroundHoverColor : this.state.settings.backgroundColor }}
      onClick={() => {
        if (!this.slickSlider) {
          return;
        }
        this.slickSlider.slickNext();
      }}
    >
      {this.getArrowIcon(
        this.state.settings.arrowType,
        false,
        isHover ? this.state.settings.hoverColor : this.state.settings.color,
      )}
      <span className="offScreen">Next image</span>
    </button>;

    return <Hover
      onMouseOut={() => getNextButton(false)}
      onMouseOver={() => getNextButton(true)}
    />;
  }

  private fixAccessibility() {
    if (!this.refSliderContainer) {
      return;
    }
    this.refSliderContainer
      .querySelectorAll(".slick-slide:not(.slick-active) a")
      .forEach((anchor) => {
        anchor.setAttribute("tabindex", "-1");
        anchor.setAttribute("aria-hidden", "true");
      });
    this.refSliderContainer
      .querySelectorAll(".slick-active a")
      .forEach((anchor) => {
        anchor.removeAttribute("tabindex");
        anchor.removeAttribute("aria-hidden");
    });
  };

  private get slider() {
    return this.state.slides.length > 0
      ? (
        <div className="slider"
          style={{ maxWidth: this.state.settings.dimensions.width }}
          ref={(container) => this.refSliderContainer = container}
          >
          {!this.state.isLoading && this.state.settings.arrows && this.state.slides.length > 1 ?
            <div className="BAN_controls">
              {this.getPrevButton()}
              {this.state.settings.slideshowMode ? this.getPauseButton() : null}
              {this.getNextButton()}
            </div> : null
          }
          <Slider
            {...this.sliderSettings}
            slickPause={this.state.isSliderPaused}
            afterChange={(index) => {
              this.setState({
                currentIndex: index,
                isSliderPaused: !this.state.slides[index].isLoaded,
              });
              this.fixAccessibility();
            }}
            onReInit={() => {
              this.fixAccessibility();
            }}
            ref={(slider) => this.slickSlider = slider}
          >
            {_.map(this.state.slides, (slide, index) => {
              const context = {
                altText: slide.alt || "",
                caption: slide.caption || "",
                title: slide.title || "",
                url: slide.url || "",
                urlTarget: slide.imageAction,
              };
              const url = this.getUrl(slide.cropped.path);
              return <Loader isLoading={!slide.isLoaded} key={index}>
                <div
                  className="BAN_slide"
                  style={{
                    backgroundImage: `url(${url})`,
                  }}
                >
                  {(!isEmptyString(slide.url || "") && index === this.state.currentIndex)
                    ? <a href={slide.url} target={slide.imageAction} title={!isEmptyString(slide.title) ? slide.title : slide.alt || ""}>
                      <img
                        src={url}
                        alt={slide.alt || ""}
                        title={slide.title || ""}
                        onLoad={() => this.onImageLoad(slide)}
                      />
                    </a>
                    : <img
                      src={url}
                      alt={slide.alt || ""}
                      title={slide.title || ""}
                      onLoad={() => this.onImageLoad(slide)}
                    />
                  }
                  {this.state.settings.customCaption
                    ? <div
                      style={{ maxWidth: this.state.settings.dimensions.width }}
                      dangerouslySetInnerHTML={{ __html: this.state.captionTemplate ? this.state.captionTemplate(context) : slide.caption }}
                    />
                    : !isEmptyString(`${slide.title || ""}${slide.caption || ""}`)
                      ? <div className="BAN_caption" style={{ maxWidth: this.state.settings.dimensions.width }}>
                        {!isEmptyString(slide.title) ? <div className="BAN_title">{slide.title}</div> : null}
                        {!isEmptyString(slide.caption) ? <p className="BAN_text">{slide.caption}</p> : null}
                      </div>
                      : null
                  }
                </div>
              </Loader>;
            })}
          </Slider>
        </div>
      ) : null;
  }

  private getArrowIcon(type: ArrowType, left: boolean, color: string) {
    let icon: string;
    switch (type) {
      case ArrowType.One:
        icon = left ? "fa-arrow-left" : "fa-arrow-right";
        break;
      case ArrowType.Two:
      default:
        icon = left ? "fa-chevron-left" : "fa-chevron-right";
        break;
    }

    return <em className={`fa ${icon}`} style={{ color }}></em>;
  }

  private get sliderSettings() {
    return {
      arrows: false,
      autoplay: this.state.settings.slideshowMode,
      autoplaySpeed: convertToMilliseconds(this.state.settings.speed),
      dots: this.state.settings.dots,
      fade: this.state.settings.slideshowMode && this.state.settings.fade,
      infinite: this.state.slides.length > 1,
      initialSlide: this.state.currentIndex,
      lazyLoad: this.state.settings.lazyLoad,
      nextArrow: null,
      pauseOnHover: true,
      prevArrow: null,
      slidesToScroll: 1,
      slidesToShow: 1,
    };
  }

  private setTempSettings() {
    const tempSettings = { ...this.state.settings };
    this.setState({ tempSettings });
  }

  private setSettings(settings, callback?: () => void) {
    settings = settings || {};
    settings = { ...settings };

    if (settings.arrowType == null) {
      settings.arrowType = ArrowType.One;
    }
    if (settings.arrows == null) {
      settings.arrows = true;
    }
    settings.backgroundColor = settings.backgroundColor || white;
    settings.backgroundHoverColor = settings.backgroundHoverColor || white;
    settings.caption = settings.caption || "";
    settings.color = settings.color || black;
    if (settings.customCaption == null) {
      settings.customCaption = false;
    }
    settings.dimensions = settings.dimensions || { height: defaultHeight, width: defaultWidth };
    settings.dimensions.height = settings.dimensions.height || defaultHeight;
    settings.dimensions.width = settings.dimensions.width || defaultWidth;
    if (settings.dots == null) {
      settings.dots = false;
    }
    /*if (settings.fade == null) {
      settings.fade = false;
    }*/
    settings.fade = false; // For now always disable fade
    settings.hoverColor = settings.hoverColor || black;
    if (settings.lazyLoad == null) {
      settings.lazyLoad = true;
    }
    if (settings.randomStartImage == null) {
      settings.randomStartImage = false;
    }
    if (settings.slideshowMode == null) {
      settings.slideshowMode = false;
    }
    settings.speed = settings.speed || sliderSpeeds[0];

    let captionTemplate = null;
    if (settings.customCaption && !isEmptyString(settings.caption)) {
      captionTemplate = Handlebars.compile(settings.caption);
    }

    this.setState({
      captionTemplate,
      settings,
    }, () => {
      if (callback) {
        callback();
      }
    });
  }

  private saveImages(cropped: Blob, transform: any) {
    this.setState({ crop: false, isLoading: true });
    if (this.state.isAddOpen) {
      uploadFile(this.props.portletInstanceId, this.state.upload.image)
        .then((res: any) => {
          let result = res;
          if (res.d) { result = res.d; } // .NET service response encloses payload in a 'd' property.
          return result.json();
        }).then((originalImageData) => {
          const originalImage = originalImageData.data;
          uploadFile(this.props.portletInstanceId, cropped)
            .then((res: any) => {
              let result = res;
              if (res.d) { result = res.d; } // .NET service response encloses payload in a 'd' property.
              return result.json();
            })
            .then((croppedImageData) => {
              const slide = _.clone(this.state.currentSlide);
              slide.cropped = croppedImageData.data;
              slide.original = { displayName: this.state.upload.image.name, ...originalImage };
              slide.transform = transform;
              slide.isLoaded = false;
              slide.url = slide.url || "";

              const slides = [...this.state.slides, slide];

              this.setState({
                slides,
                currentSlide: null,
                hasUnsavedChanges: true,
                isAddOpen: false,
                isEditOpen: false,
                isLoading: false,
              });

              this.clearUpload();
            });
        });
    } else {
      uploadFile(this.props.portletInstanceId, cropped)
        .then((res: any) => {
          let result = res;
          if (res.d) { result = res.d; } // .NET service response encloses payload in a 'd' property.
          return result.json();
        })
        .then((croppedImageData) => {
          const index = _.findIndex(this.state.slides, (slide) => slide.id === this.state.currentSlide.id);
          const slide = _.clone(this.state.currentSlide);
          slide.cropped = croppedImageData.data;
          slide.transform = transform;
          slide.isLoaded = false;
          slide.url = slide.url || "";

          const slides = [...this.state.slides];
          slides[index] = slide;

          this.setState({
            slides,
            currentSlide: null,
            hasUnsavedChanges: true,
            isAddOpen: false,
            isEditOpen: false,
            isLoading: false,
          });

          this.nextReCropSlide();
        });
    }
  }

  private addSlide() {
    this.setState({
      isAddOpen: true,
      isLoading: true,
    });
  }

  private editSlide(slide) {
    this.setState({
      currentSlide: _.clone(slide),
      isEditOpen: true,
      isLoading: true,
    });
  }

  private removeSlide(slide) {
    this.setState({ isLoading: true });
    const slides = [...this.state.slides];
    _.remove(slides, (i) => i.id === slide.id);
    this.setState({
      hasUnsavedChanges: true,
      isLoading: false,
      slides,
    });
  }

  private onSortEnd = ({ oldIndex, newIndex }) => {
    const { slides } = this.state;
    this.setState({
      hasUnsavedChanges: true,
      slides: arrayMove(slides, oldIndex, newIndex),
    });
  }
};
